import type { User } from './auth-client';

/** Synthetic email assigned to Telegram-only users: telegram_<id>@users.uztender.com */
const TELEGRAM_EMAIL_RE = /^telegram_\d+@users\.uztender\.com$/i;

type DisplayUser = Pick<User, 'name' | 'email'>;

/**
 * Human-friendly account label:
 *  - the user's name (Telegram first+last name, or any profile name) when set;
 *  - "Telegram" for a Telegram-only user with no name (synthetic placeholder email);
 *  - otherwise the email (normal password/Google accounts).
 *
 * This keeps the account chip from showing the ugly synthetic
 * `telegram_<id>@users.uztender.com` address for Telegram logins.
 */
/**
 * Is this account still on the synthetic Telegram address — i.e. has it no
 * mailbox we can reach at all?
 *
 * The one test, shared by everything that has to ask. A placeholder cannot
 * receive an invoice, a receipt, a renewal reminder or a login code, so it is
 * not «почта, которую забыли подтвердить» — it is the absence of an address,
 * and the UI offers to ADD one rather than to confirm one.
 *
 * Mirrors the server's `isRealEmail` (negated); if the two ever disagree the
 * dashboard offers a step the API refuses.
 */
export function isPlaceholderEmail(email: string | null | undefined): boolean {
  return !!email && TELEGRAM_EMAIL_RE.test(email);
}

export function userDisplayName(user: DisplayUser): string {
  const name = user.name?.trim();
  if (name) return name;
  if (TELEGRAM_EMAIL_RE.test(user.email)) return 'Telegram';
  return user.email;
}

/**
 * Whether the user has an active paid plan. GET /auth/me only sets
 * `subscription` for an active, non-expired, non-free plan, so its presence is
 * the signal. Used to show the real plan and hide upgrade prompts.
 */
export function isPaidUser(user: Pick<User, 'subscription'> | null | undefined): boolean {
  return Boolean(user?.subscription);
}

/**
 * Short plan label for the UI: "Pro" / "Business" / "ИП".
 *
 * ⚠️ The fallback returns the RAW KEY, which is a database identifier — an ИП
 * subscriber's settings page read «Тариф: ip-monthly» until `ip` was mapped
 * here. Anywhere this can render, a missing prefix shows the key to a customer,
 * so add new plan families to this list, not to the call site.
 */
export function planLabel(user: Pick<User, 'subscription'> | null | undefined): string | null {
  const plan = user?.subscription?.plan;
  if (!plan) return null;
  if (plan.startsWith('pro')) return 'Pro';
  if (plan.startsWith('business')) return 'Business';
  if (plan.startsWith('ip')) return 'ИП';
  return plan;
}

/** 1–2 char avatar initials derived from the display name. */
export function userInitials(user: DisplayUser): string {
  const display = userDisplayName(user).trim();
  const words = display.split(/\s+/).filter(Boolean);
  if (words.length >= 2) return (words[0][0] + words[1][0]).toUpperCase();
  return display.slice(0, 2).toUpperCase();
}
